import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { Typography } from 'design-system/src/components/Typography/Typography';
import Button from 'design-system/src/components/Button/Button';
import { Popover, PopoverButton, PopoverPanel } from 'design-system/src/components/Popover/Popover';
import { standardTransitionStyles, focusVisibleInsetStyles } from 'design-system/src/sharedStyles';
import { Link } from 'react-router-dom';
import { client } from '../../../api';
import { formatSimpleDate } from 'shared-utils/utils/formatDate';

const StyledPopoverButton = styled(PopoverButton)`
  all: unset;
`;

const EnrollmentList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const EnrollmentItem = styled(Typography)`
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  padding: 8px 12px;
  border-radius: 4px;
  ${standardTransitionStyles}

  &:hover {
    background-color: var(--grayscale-50);
    cursor: pointer;
  }

  ${focusVisibleInsetStyles}
`;

type PendingEnrollment = {
  id: string;
  created_at: string;
  patient_first_name: string;
  patient_last_name: string;
  drug_name: string;
};

/**
 * Button that opens a popover with pending enrollment forms that haven't been matched to a prescription
 */
export const PendingEnrollmentForms = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['pending-enrollment-forms'],
    queryFn: async () => {
      const res = await client.get('/enrollments/pending');
      return res.data;
    },
  });

  const pendingEnrollments = data?.pending_enrollments ?? [];
  const hasEnrollments = pendingEnrollments.length > 0;

  return (
    <Popover>
      <StyledPopoverButton>
        <Button variant="secondary" size="sm" disabled={!hasEnrollments && !isLoading}>
          Pending forms
          {isLoading ? <LoadingSpinner height="15px" size="15px" /> : ` (${pendingEnrollments.length})`}
        </Button>
      </StyledPopoverButton>
      <PopoverPanel anchor="bottom end" className="!w-[344px]">
        <EnrollmentList className="max-h-[252px] overflow-y-auto">
          {pendingEnrollments.map((enrollment: PendingEnrollment) => (
            <li key={enrollment.id}>
              <EnrollmentItem styledAs="bodySmallDMSans" to={`/enrollments/pending/${enrollment.id}`} renderedAs={Link}>
                <div>
                  <strong>
                    {enrollment.patient_first_name} {enrollment.patient_last_name}
                  </strong>{' '}
                  for <strong>{enrollment.drug_name}</strong>
                </div>
                {formatSimpleDate(enrollment.created_at)}
              </EnrollmentItem>
            </li>
          ))}
        </EnrollmentList>
      </PopoverPanel>
    </Popover>
  );
};

export default PendingEnrollmentForms;
